import ApiCodeBlock from "./DocumentationCodeBlock";
import MonitoringGoalsTable from "./DocumentationMonitoringGoalsTable";

export default function ContinuousSection() {
  return (
    <div className="space-y-5 pb-4 text-neutral-950">
      <h1 className="text-lg leading-6 font-bold -tracking-wide">
        Continuous Monitoring
      </h1>
      <div className="space-y-4">
        <div className="space-y-2">
          <h1
            id="polling-frequency"
            className="text-[18px] leading-5 font-bold -tracking-wide"
          >
            Polling Frequency & Frequency
          </h1>
          <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-600">
            Contrary to legacy documentation, there is no enforced 1-hour
            minimum interval. Your polling strategy should be driven by your
            specific plan's capacity.
          </p>

          <div className="my-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-bold">Plan-Based Logic</h3>
              <p className="text-xs leading-relaxed text-neutral-500">
                Instead of waiting 60 minutes, calculate your frequency based on
                your <code>calls_per_min</code>. If you have 100 requests/min,
                you can poll every few seconds if necessary.
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <h3 className="mb-2 text-sm font-bold">Data Freshness</h3>
              <p className="text-xs leading-relaxed text-neutral-500">
                Use the <code>last_24_hours=true</code> flag combined with
                frequent polling to capture real-time updates without hitting
                the "per hour" documentation myth.
              </p>
            </div>
          </div>

          <p className="text-sm font-bold -tracking-wide">
            Optimized Polling Checklist:
          </p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li className="text-sm leading-[140%] font-medium">
              <strong>Ignore the 1-hour rule:</strong> Scale your requests
              according to your <code>X-RateLimit-Limit</code>.
            </li>
            <li className="text-sm leading-[140%] font-medium">
              <strong>Batch Keywords:</strong> Combine multiple terms into a
              single request using repeatable <code>include_keywords</code>{" "}
              params to save quota.
            </li>
            <li className="text-sm leading-[140%] font-medium">
              <strong>Status Monitoring:</strong> Use the{" "}
              <code>date_updated</code> field to identify if existing records
              have changed since your last poll.
            </li>
          </ul>
        </div>
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <h1
            id="pagination"
            className="text-[18px] leading-5 font-bold -tracking-wide"
          >
            Pagination Management
          </h1>
          <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-600">
            The API uses page-based pagination. Control your results using the{" "}
            <code>page</code> and <code>limit</code> query parameters.
          </p>

          <div className="my-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
            <p className="mb-1 text-xs font-bold tracking-wider text-neutral-500 uppercase">
              Constraints
            </p>
            <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
              <li>Default Limit: 10</li>
              <li>Max Limit: 100</li>
              <li>Default Page: 1</li>
            </ul>
          </div>

          <p className="text-sm font-bold -tracking-wide">
            Implementation Guidelines:
          </p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li className="text-sm leading-[140%] font-medium">
              Use <code>has_next</code> from the <code>metadata</code> object to
              determine if more data is available.
            </li>
            <li className="text-sm leading-[140%] font-medium">
              Manually increment the <code>page</code> parameter for subsequent
              requests.
            </li>
            <li className="text-sm leading-[140%] font-medium">
              Do not rely on result counts alone; <code>has_next</code> is the
              definitive source for continuation.
            </li>
          </ul>

          <p className="mt-4 text-sm font-bold -tracking-wide">
            Correct Pagination Example:
          </p>
          <ApiCodeBlock
            language="javascript"
            code={`async function fetchAllPages(baseUrl, limit = 50) {
  let allPosts = [];
  let currentPage = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await fetch(\`\${baseUrl}?page=\${currentPage}&limit=\${limit}\`, {
      headers: { "X-API-Token": "your-api-key" }
    });
    const { data, metadata } = await response.json();

    // Data contains platform-specific arrays (facebook, youtube, etc.)
    allPosts.push(data); 
    
    hasMore = metadata.has_next;
    currentPage++;
  }

  return allPosts;
}`}
          />
        </div>
      </div>
      <div className="space-y-2">
        <h1
          id="sorting"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Strategic Sort Field Selection
        </h1>
        <p className="text-sm leading-[140%] font-medium tracking-normal">
          Choose the appropriate sorting method based on your monitoring
          objectives:{" "}
        </p>
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Sort by{" "}
          <span className="font-mono font-medium text-[#079300]">
            indexed_at
          </span>{" "}
          (Recommended for most cases):
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            <span className="text-sm leading-[140%] font-bold">Use when: </span>
            You need chronological order of new content
          </li>
          <li className="text-sm leading-[140%] font-medium">
            <span className="text-sm leading-[140%] font-bold">Benefits: </span>
            Guarantees latest indexed posts first
          </li>
          <li className="text-sm leading-[140%] font-medium">
            <span className="text-sm leading-[140%] font-bold">Best for: </span>
            Real-time monitoring, trend analysis, news feeds
          </li>
        </ul>
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Sort by{" "}
          <span className="font-mono font-medium text-[#079300]">
            updated_at
          </span>{" "}
          (Use with caution):
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            <span className="text-sm leading-[140%] font-bold">Use when: </span>
            You need to track content modifications and updates
          </li>
          <li className="text-sm leading-[140%] font-medium">
            <span className="text-sm leading-[140%] font-bold">
              Considerations:{" "}
            </span>
            May return duplicate posts with different timestamps
          </li>
          <li className="text-sm leading-[140%] font-medium">
            <span className="text-sm leading-[140%] font-bold">Best for: </span>
            Content change tracking, engagement monitoring
          </li>
        </ul>
        <MonitoringGoalsTable />
      </div>
      <div className="space-y-2">
        <h1
          id="smart-search"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Smart Search Pattern Usage
        </h1>
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Full Term Optimization:
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            Always prioritize exact terms over wildcards when possible
          </li>
          <li className="text-sm leading-[140%] font-medium">
            System learns from full search terms to optimize future data
            collection
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Performance benefit from predictable search patterns
          </li>
        </ul>
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Limited Wildcard Implementation:
        </p>
        <ApiCodeBlock
          endpoint={[
            "# Recommended: Combine wildcards with full terms",
            "GET /api/v2/search?terms=cybersecurity AND attack*&type=combined",
            "",
            "# Avoid: Wildcard-only searches",
            "GET /api/v2/search?terms=*security*&type=wildcard_only",
          ]}
        />
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Wildcard Best Practices:
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            Use wildcards sparingly and strategically
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Always combine with at least one full search term
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Monitor performance impact and adjust accordingly
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Consider using synonym lists instead of broad wildcards
          </li>
        </ul>
      </div>
      <div className="space-y-2">
        <h1
          id="implementation-checklist"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Monitoring Implementation Checklist
        </h1>
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Pre-Implementation:
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            Define monitoring objectives and success metrics
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Set up proper error handling and retry mechanisms
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Implement logging for API calls and responses
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Configure alert systems for monitoring failures
          </li>
        </ul>

        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          During Operation:
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            Monitor API rate limits and adjust polling frequency
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Track data quality and completeness metrics
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Regular review of search term effectiveness
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Performance optimization based on usage patterns
          </li>
        </ul>
        <p className="text-sm leading-[140%] font-bold -tracking-wide">
          Maintenance Schedule:
        </p>
        <ul className="list-inside list-disc pl-2">
          <li className="text-sm leading-[140%] font-medium">
            Weekly review of monitoring logs and alerts
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Monthly analysis of data collection patterns
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Quarterly optimization of search terms and intervals
          </li>
          <li className="text-sm leading-[140%] font-medium">
            Annual review of monitoring strategy alignment with business goals
          </li>
        </ul>
      </div>
    </div>
  );
}
